//src/pages/inventory/index.jsx
import { Box, Typography, Tab } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "../../components/PageShell";
import Pharmacy from "./Pharmacy/index.jsx";
import Product from "./Product/index.jsx";
import PurchaseInvoice from "./PurchaseInvoice/index.jsx";
import MedicineRequestsList from "./Pharmacy/MedicineRequestsList.jsx";
import Prescriptions from "./Pharmacy/Prescriptions.jsx";
import IpdTreatments from "./Pharmacy/IpdTreatments.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import {
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from "../../utils/pageLayoutStyles";
import {
  isPathModuleStaffRole,
  PATH_INVENTORY_CAPABILITY,
  PATHOLOGY_INVENTORY_LABEL,
} from "../../utils/pathRoleUtils";

const InventoryPage = () => {
  const { user } = useAuth();
  const role = String(user?.roleName || "").toLowerCase();
  const isPharmacyAdmin = role === "pharmacy admin";
  const isAdmin =
    role === "admin" || role === "superadmin" || role === "super_admin";
  const isPathOnlyRole = isPathModuleStaffRole(role);

  const {
    hasCapability,
    hasPathPackageAccess,
    loading: capabilitiesLoading,
  } = useSubscriptionCapabilities();
  const hasMedicineInventoryCapability = hasCapability("Medicine Inventory");
  const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");
  const hasPharmacyCapability = hasCapability("Pharmacy");
  const hasPathInventoryCapability =
    hasPathPackageAccess && hasCapability(PATH_INVENTORY_CAPABILITY);

  // Pharmacy patients tab: admin/superadmin only (pharmacy admin uses Manage Your Patients)
  const shouldShowPharmacyTab =
    !isPharmacyAdmin &&
    hasPharmacyCapability &&
    (hasIpdCapability || isAdmin);

  const availableTabs = useMemo(() => {
    if (isPathOnlyRole) {
      return hasPathPackageAccess
        ? [{ name: PATHOLOGY_INVENTORY_LABEL, component: "pathdesk" }]
        : [];
    }

    if (!isPharmacyAdmin && !isAdmin) {
      return [];
    }

    const tabs = [];
    if (shouldShowPharmacyTab) {
      tabs.push({ name: "Pharmacy", component: "pharmacy" });
    }
    if (hasMedicineInventoryCapability || isPharmacyAdmin) {
      tabs.push({ name: "Medicine", component: "medicine" });
    }
    if (hasIpdCapability && (isAdmin || isPharmacyAdmin)) {
      tabs.push({ name: "Medicine Requests", component: "medicineRequests" });
    }
    if (hasPathInventoryCapability && !isPharmacyAdmin) {
      tabs.push({ name: PATHOLOGY_INVENTORY_LABEL, component: "pathInventory" });
    }
    return tabs;
  }, [
    isPathOnlyRole,
    hasPathPackageAccess,
    isPharmacyAdmin,
    isAdmin,
    shouldShowPharmacyTab,
    hasMedicineInventoryCapability,
    hasIpdCapability,
    hasPathInventoryCapability,
  ]);

  const defaultTab = useMemo(() => {
    if (!availableTabs.length) return "";
    const medicine = availableTabs.find((t) => t.component === "medicine");
    return medicine?.component || availableTabs[0].component;
  }, [availableTabs]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientType, setPatientType] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    if (capabilitiesLoading || !defaultTab) return;
    const tabFromUrl = String(searchParams.get("tab") || "").trim();
    const urlTabValid =
      tabFromUrl && availableTabs.some((t) => t.component === tabFromUrl);
    if (urlTabValid && selectedTab !== tabFromUrl) {
      setSelectedTab(tabFromUrl);
      return;
    }
    const stillValid = availableTabs.some((t) => t.component === selectedTab);
    if (!stillValid) {
      setSelectedTab(defaultTab);
    }
  }, [capabilitiesLoading, availableTabs, selectedTab, defaultTab, searchParams]);

  const handleTabChange = (_event, newValue) => {
    setSelectedTab(newValue);
    setShowTimeline(false);
    setSelectedPatientId(null);
    setPatientType(null);
    const next = new URLSearchParams(searchParams);
    next.set("tab", newValue);
    setSearchParams(next, { replace: true });
  };

  const handlePatientClick = (patientId, type) => {
    setSelectedPatientId(patientId);
    setPatientType(type);
    setShowTimeline(true);
  };

  const handleBackToPatientList = () => {
    setShowTimeline(false);
    setSelectedPatientId(null);
    setPatientType(null);
  };

  if (capabilitiesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (
    !hasMedicineInventoryCapability &&
    !(isPathOnlyRole && hasPathPackageAccess) &&
    !isPharmacyAdmin
  ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400} p={3}>
        <Typography variant="h6" color="error">
          Medicine Inventory is not available in your subscription plan.
        </Typography>
      </Box>
    );
  }

  if (availableTabs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400} p={3}>
        <Typography variant="h6" color="error">
          Inventory is not available in your subscription plan.
        </Typography>
      </Box>
    );
  }

  const activeTab = selectedTab || defaultTab;
  if (!activeTab) {
    return null;
  }

  return (
    <PageShell title="INVENTORY" paperSx={{ overflow: "hidden" }}>
        <TabContext value={activeTab}>
          <Box
            sx={{
              ...PAGE_TABS_BAR_SX,
              px: { xs: 1, sm: 2 },
            }}
          >
            <TabList
              onChange={handleTabChange}
              {...PAGE_TAB_LIST_PROPS}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="inventory information tabs"
              sx={{
                minHeight: 48,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  minHeight: 48,
                  px: 2,
                },
              }}
            >
              {availableTabs.map((tab) => (
                <Tab
                  key={tab.component}
                  label={tab.name}
                  value={tab.component}
                />
              ))}
            </TabList>
          </Box>

          <TabPanel value="medicine" sx={{ p: 1.5, px: 0 }}>
            <Product hideMainTitle />
          </TabPanel>

          <TabPanel value="medicineRequests" sx={{ p: 1.5, px: 0 }}>
            <MedicineRequestsList />
          </TabPanel>

          <TabPanel value="pathInventory" sx={{ p: 1.5, px: 0 }}>
            <PurchaseInvoice hideMainTitle />
          </TabPanel>

          <TabPanel value="pathdesk" sx={{ p: 1.5, px: 0 }}>
            <PurchaseInvoice hideMainTitle />
          </TabPanel>

          <TabPanel value="pharmacy" sx={{ p: 1.5, px: 0 }}>
            {showTimeline && selectedPatientId ? (
              patientType === "ipd" ? (
                <IpdTreatments
                  patientId={selectedPatientId}
                  onBack={handleBackToPatientList}
                />
              ) : (
                <Prescriptions
                  patientId={selectedPatientId}
                  onBack={handleBackToPatientList}
                />
              )
            ) : (
              <Pharmacy onPatientClick={handlePatientClick} hideMainTitle />
            )}
          </TabPanel>
        </TabContext>
    </PageShell>
  );
};

export default InventoryPage;
